import { useTranslation } from 'react-i18next';
import {
    BarChart3,
    DollarSign,
    FileText,
    Search,
    Users,
} from 'lucide-react';
import { ScoreCard, SignalCard } from '../../dashboard';
import type { FacilityMetricsResult } from '../../../services/api/facilityMetricsService';
import {
    toSignalCardProps,
    type SignalDisplayConfig,
} from '../signalCardProps';
import PeriodPicker from './PeriodPicker';
import type { PeriodState } from './periodUtils';

interface SubscriptionOverviewProps {
    metrics: FacilityMetricsResult;
    selectedPeriod: PeriodState;
    onPeriodChange: (period: PeriodState) => void;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
});

export default function SubscriptionOverview({
    metrics,
    selectedPeriod,
    onPeriodChange,
}: SubscriptionOverviewProps) {
    const { t } = useTranslation();

    const SUBSCRIPTION_SIGNAL_CONFIGS: Record<string, SignalDisplayConfig> = {
        activeUsers: {
            icon: <Users className="h-4 w-4" aria-hidden="true" />,
            tooltip: t('facilities.subscription.tooltips.activeUsersSelected'),
        },
        activeUsersVsLicensedPercent: {
            icon: <BarChart3 className="h-4 w-4" aria-hidden="true" />,
            tooltip: t(
                'facilities.subscription.tooltips.activeUsersVsLicensed'
            ),
        },
        searchesOverTime: {
            icon: <Search className="h-4 w-4" aria-hidden="true" />,
            tooltip: t('facilities.subscription.tooltips.searchVolume'),
            visual: 'sparkline',
            trendFormatter: (v) =>
                t('facilities.subscription.searchCount', { count: v }),
        },
        // CPT configs — US-only, not translated
        cptReportsCreated: {
            icon: <FileText className="h-4 w-4" aria-hidden="true" />,
            tooltip: 'Total completed CPT reports.',
            visual: 'bars',
            subtitle: 'reports',
        },
        cptEstimatedReimbursement: {
            icon: <DollarSign className="h-4 w-4" aria-hidden="true" />,
            tooltip:
                'Estimated reimbursement based on configured CPT rates and total CPT code volume.',
            visual: 'bars',
            valueFormatter: (value) => currencyFormatter.format(value),
            breakdownValueFormatter: (value) =>
                currencyFormatter.format(value),
            subtitle: 'estimate',
        },
    };

    const visibleSignals = Array.isArray(metrics.signals)
        ? metrics.signals
        : [];

    const signalLabelOverrides: Record<string, string> = {
        activeUsers: t('facilities.subscription.activeUsers'),
        searchesOverTime: t('facilities.subscription.searches'),
        activeUsersVsLicensedPercent: t(
            'facilities.subscription.signalLabels.activeUsersVsLicensedPercent'
        ),
    };

    const cardItems = visibleSignals.map((signal) => ({
        key: signal.key,
        props: toSignalCardProps(
            signalLabelOverrides[signal.key]
                ? { ...signal, label: signalLabelOverrides[signal.key] }
                : signal,
            SUBSCRIPTION_SIGNAL_CONFIGS[signal.key]
        ),
    }));

    const isFiveLayout = cardItems.length === 5;
    const isCompactScoreLayout = cardItems.length === 3;

    return (
        <div className="mb-12">
            <div className="mb-3 flex items-center justify-end">
                <PeriodPicker
                    period={selectedPeriod}
                    onChange={onPeriodChange}
                />
            </div>
            <div className="flex flex-col gap-3 lg:flex-row">
                <ScoreCard
                    title={t('facilities.subscription.valueRealization')}
                    score={metrics.score}
                    layout={isCompactScoreLayout ? 'horizontal' : 'vertical'}
                    className="lg:w-72 lg:shrink-0"
                />
                <div
                    className={`grid flex-1 gap-3 ${
                        isFiveLayout
                            ? 'grid-cols-2 lg:grid-cols-6'
                            : 'md:grid-cols-2 xl:grid-cols-3'
                    }`}
                >
                    {cardItems.map((item, index) => {
                        const spanClass = isFiveLayout
                            ? index < 3
                                ? 'lg:col-span-2'
                                : 'lg:col-span-3'
                            : '';

                        return (
                            <SignalCard
                                key={item.key}
                                {...item.props}
                                className={`${item.props.className ?? ''} ${spanClass}`}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
